import { Localized } from "@fluent/react"
import { Button, IconButton, Menu, MenuItem, TableCell, TableRow } from "@mui/material"
import { AppId } from "../../app/appTypes"
import { UrsGroup } from "./UrsSlice"
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { useState } from "react"
import { If } from "../../app/If"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

export interface GroupProps {
    group: UrsGroup
    handleAddRequirement: (groupId: AppId) => void
    handleAddFromCatalog: (groupId: AppId) => void
    handleEditGroup: (group: UrsGroup) => void
    handleDeleteGroup: (group: UrsGroup) => void
    hasAnyRequirement: boolean
    canEdit: boolean
    canReorder: boolean
    showSupplierComments: boolean
    onDragOver?: (e: React.DragEvent<HTMLTableRowElement>) => void
}

export const Group = (props: GroupProps) => {
    const { group, showSupplierComments } = props
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [rightMenuAnchorEl, setRightMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl)
    const rightMenuOpen = Boolean(rightMenuAnchorEl)
    const { setNodeRef, isOver, listeners, attributes, transform, transition } = useSortable({
        id: group.id,
        data: { type: 'group' },
        disabled: !props.canReorder,
    })

    return <TableRow
        key={group.id}
        ref={setNodeRef}
        // TODO: spread attributes only when canReorder is true. When reordering is
        // disabled (e.g. ImportedRequirementsPanel) this adds role="button"/tabIndex/
        // aria-roledescription="sortable" to a non-draggable row. a11y polish, deferred.
        {...attributes}
        sx={{
            backgroundColor: isOver ? '#dbeafe' : '#f1f1f1',
            transform: CSS.Transform.toString(transform) ?? undefined,
            transition: transition ? `${transition}, background-color 150ms` : 'background-color 150ms',
        }}
    >
        <TableCell
            sx={{
                width: 32,
                maxWidth: 32,
                padding: '0 4px',
                cursor: props.canReorder ? 'grab' : 'default',
                touchAction: 'none',
            }}
            {...(props.canReorder && listeners ? listeners : {})}
        >
            {props.canReorder && (
                <DragIndicatorIcon fontSize="small" sx={{ color: 'action.disabled', display: 'block' }} />
            )}
        </TableCell>
        <TableCell colSpan={showSupplierComments ? 4 : 2}>{`${group.numeration}. ${group.name}`}&nbsp;
            {props.canEdit && <><IconButton onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}><MoreVertIcon /></IconButton>
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={() => { props.handleEditGroup(group); setAnchorEl(null) }}><Localized id='edit'>Edytuj</Localized></MenuItem>
                <MenuItem disabled={props.hasAnyRequirement} onClick={() => { props.handleDeleteGroup(group); setAnchorEl(null) }}><Localized id='delete'>Usuń</Localized></MenuItem>
            </Menu></>}
        </TableCell>
        <TableCell sx={{ textAlign: 'right' }} colSpan={2}>
            <If condition={props.canEdit}>
                <Button size="small" onClick={() => props.handleAddRequirement(group.id)} startIcon={<AddIcon />}><Localized id='add'>Dodaj</Localized></Button>
                <IconButton onClick={(event: React.MouseEvent<HTMLButtonElement>) => setRightMenuAnchorEl(event.currentTarget)}><MoreVertIcon /></IconButton>
                {<Menu anchorEl={rightMenuAnchorEl} open={rightMenuOpen} onClose={() => setRightMenuAnchorEl(null)}>
                    <MenuItem onClick={() => { props.handleAddFromCatalog(group.id); setRightMenuAnchorEl(null) }}><Localized id='urs-add-from-catalog'>Dodaj wymaganie z katalogu</Localized></MenuItem>
                </Menu>}
            </If>
        </TableCell>
    </TableRow>
}

export interface AddToGroupRowProps {
    // The add dialog's target section. For requirements sitting directly under a
    // requirement type this is the type's own id — the backend takes whichever
    // section contains the requirements, group or type.
    groupId: AppId
    groupName: string
    onAdd: (groupId: AppId) => void
    showSupplierComments: boolean
}

// Closes a list of requirements with a second way in to the same add dialog the
// header above them offers. On a long list that header — and its Add button —
// has scrolled away by the time the user reaches the end, which is exactly where
// they decide another requirement is missing.
export const AddToGroupRow = ({ groupId, groupName, onAdd, showSupplierComments }: AddToGroupRowProps) =>
    <TableRow sx={{ '& td': { paddingTop: 1, paddingBottom: 1 } }}>
        <TableCell sx={{ width: 32, maxWidth: 32, padding: '0 4px' }} />
        <TableCell colSpan={showSupplierComments ? 6 : 4}>
            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onAdd(groupId)}
                sx={{
                    textTransform: 'none',
                    fontWeight: 400,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                }}
            >
                <Localized id="urs-add-to-group" vars={{ group: groupName }}>
                    <span>{`Dodaj do grupy: ${groupName}`}</span>
                </Localized>
            </Button>
        </TableCell>
    </TableRow>

